/**
 * free-shipping-confetti.js
 *
 * Watches the cart drawer's progress bar and fires a black confetti burst
 * once per upward threshold crossing — i.e. when the cart total moves from
 * below the free-shipping threshold to at-or-above. Stays silent on the
 * page load even if the cart already qualifies, and stays silent on
 * subsequent updates that don't cross the threshold.
 *
 * Reads state from data-attrs on .free-shipping-progress (set by the Vue
 * binding in right-drawer.liquid):
 *   data-fs-threshold — cents required for free shipping
 *   data-fs-total     — current cart subtotal in cents
 *   data-fs-met       — "true" once total >= threshold
 *
 * MutationObserver picks up the Vue-driven attribute updates without us
 * having to hook into the Vue instance.
 */
(function () {
  'use strict';

  function readState(el) {
    return {
      total: parseInt(el.getAttribute('data-fs-total') || '0', 10) || 0,
      threshold: parseInt(el.getAttribute('data-fs-threshold') || '0', 10) || 0,
      met: el.getAttribute('data-fs-met') === 'true'
    };
  }

  function fireBlackConfetti() {
    if (typeof window.confetti !== 'function') return;
    // Drop the confetti from the top edge of the open cart drawer so it
    // rains down through the mini-cart instead of bursting up from the
    // page corners. Falls back to a top-center burst if the drawer isn't
    // mounted for some reason.
    var drawer = document.querySelector('.drawer--right.drawer--cart');
    var originX = 0.5;
    if (drawer) {
      var r = drawer.getBoundingClientRect();
      originX = (r.left + r.width / 2) / window.innerWidth;
    }
    var defaults = {
      colors: ['#000000', '#1a1a1a', '#333333'],
      ticks: 300,
      gravity: 1.1,
      scalar: 0.9,
      shapes: ['square', 'circle'],
      angle: 270,           // shoot straight down
      startVelocity: 18,    // gentle drift, gravity does the falling
      spread: 90,           // spread across the drawer width
      origin: { x: originX, y: 0 },
      zIndex: 9999          // sit above the cart drawer (theme z-index 1500)
    };
    // Two staggered bursts for a fuller rain effect.
    window.confetti(Object.assign({}, defaults, { particleCount: 60 }));
    setTimeout(function () {
      window.confetti(Object.assign({}, defaults, { particleCount: 60 }));
    }, 250);
  }

  // Persistent "last seen cart total" so an upward crossing is detected
  // even when the drawer is destroyed/re-mounted (Vue re-renders) between
  // the two states. Survives page reloads within a session.
  var STORE_KEY = '__fs_last_total_cents';

  function getStoredTotal() {
    try {
      var v = sessionStorage.getItem(STORE_KEY);
      return v === null ? -1 : parseInt(v, 10);
    } catch (e) { return -1; }
  }
  function setStoredTotal(v) {
    try { sessionStorage.setItem(STORE_KEY, String(v)); } catch (e) {}
  }

  function maybeFire(prevTotal, state) {
    // Only fire on an UPWARD crossing of the threshold. First-ever observation
    // (prevTotal === -1) is treated as "no prior state" — silent.
    if (prevTotal >= 0 && prevTotal < state.threshold && state.total >= state.threshold) {
      fireBlackConfetti();
    }
  }

  function attach(el) {
    if (el.__fsWatched) return;
    el.__fsWatched = true;
    var state = readState(el);
    maybeFire(getStoredTotal(), state);
    setStoredTotal(state.total);

    var observer = new MutationObserver(function () {
      var s = readState(el);
      maybeFire(getStoredTotal(), s);
      setStoredTotal(s.total);
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['data-fs-total', 'data-fs-met']
    });
  }

  // The .free-shipping-progress element is inside the Vue drawer template,
  // which is only mounted into the DOM after the user opens the drawer.
  // Watch document.body for it to appear, then attach.
  function findAndAttach() {
    document.querySelectorAll('.free-shipping-progress').forEach(attach);
  }

  findAndAttach();
  var bodyObserver = new MutationObserver(findAndAttach);
  bodyObserver.observe(document.body, { childList: true, subtree: true });
})();
